import type {GridApi, PdfFontFamilyDefinition, ProcessCellForExportParams} from 'ag-grid-enterprise';
import Translator from "@/utils/common/Translator.ts";
import AGGridRefOrGenRefColumn from "@/utils/UIConstants/AGGridColumns/AGGridRefOrGenRefColumn.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";

const FONT_URL = 'https://cdn.jsdelivr.net/npm/@expo-google-fonts/ibm-plex-sans-arabic@0.2.3/IBMPlexSansArabic_';

interface ExportOptions {
  fileName?: string;
}

class GridExporter {
  private pdfFont?: Promise<PdfFontFamilyDefinition>;

  // Callers fire and forget from toolbar clicks, so failures (e.g. the font CDN is unreachable) are reported here.
  public async exportToPDF(gridApi: GridApi | undefined, options?: ExportOptions): Promise<void> {
    try {
      if (!gridApi)
        throw new Error('GridApi is required for export operation');
      gridApi.exportDataAsPdf({
        fileName: options?.fileName || 'grid-export.pdf',
        fonts: [await this.loadPdfFont()],
        defaultCellStyle: {fontFamily: 'IBM Plex Sans Arabic', fontSize: 10, padding: 4},
        language: Translator.isArabic() ? 'ar' : 'en',
        processCellCallback: this.cellText,
        page: {size: this.pageSizeFittingColumns(gridApi)},
      });
    } catch (e) {
      console.error(e);
      useErrorsStore().pushErrorStr(Translator.translateId('pdfExportFailed'));
    }
  }

  // AG Grid never splits columns across pages, it shrinks them to the page width; a wide grid on A4 becomes unreadable.
  private pageSizeFittingColumns(gridApi: GridApi) {
    const A4_LANDSCAPE = {width: 842, height: 595};
    const MARGINS = 72;
    const PX_TO_PT = 0.75;
    const columnsWidth = gridApi.getAllDisplayedColumns().reduce((sum, column) => sum + column.getActualWidth(), 0) * PX_TO_PT;
    return {...A4_LANDSCAPE, width: Math.max(A4_LANDSCAPE.width, Math.ceil(columnsWidth) + MARGINS)};
  }

  public cellText(params: ProcessCellForExportParams): string {
    if (!params.value)
      return params.value;
    const formattedValue = params.formatValue(params.value);
    if (typeof formattedValue === 'object')
      return AGGridRefOrGenRefColumn.nameOfRef(formattedValue);
    return formattedValue;
  }

  private loadPdfFont(): Promise<PdfFontFamilyDefinition> {
    this.pdfFont ??= Promise.all([this.fetchFont('400Regular'), this.fetchFont('700Bold')])
      .then(([regular, bold]) => ({
        family: 'IBM Plex Sans Arabic',
        faces: [{data: regular, weight: 400}, {data: bold, weight: 700}],
      }));
    this.pdfFont.catch(() => this.pdfFont = undefined);
    return this.pdfFont;
  }

  private async fetchFont(face: string): Promise<ArrayBuffer> {
    const response = await fetch(`${FONT_URL}${face}.ttf`);
    if (!response.ok)
      throw new Error(`Failed to load PDF font ${face}`);
    return response.arrayBuffer();
  }
}

export default new GridExporter();
